import React, { useState, useEffect } from 'react';
import { X, User, MessageSquare, ArrowRight, Clock, Users } from 'lucide-react';
import { api, useAuthStore } from '../auth/authStore';
import toast from 'react-hot-toast';

interface TaskReallocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  taskId: string | null;
  taskTitle?: string;
  currentRole?: 'preparer' | 'reviewer' | 'submitter';
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  roles?: string[];
  isCurrentlyAssigned?: boolean;
  lastWorkedAt?: string;
}

interface PreviouslyWorkedUser extends User {
  roles: string[];
  isCurrentlyAssigned?: boolean;
  lastWorkedAt: string;
}

export const TaskReallocationModal: React.FC<TaskReallocationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  taskId,
  taskTitle,
  currentRole = 'preparer'
}) => {
  const { user: currentUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [previouslyWorkedUsers, setPreviouslyWorkedUsers] = useState<PreviouslyWorkedUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'preparer' | 'reviewer' | 'submitter'>(currentRole);
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen && taskId) {
      // Debug: Check current user info
      console.log('Current user from auth store:', JSON.stringify(useAuthStore.getState().user, null, 2));
      fetchUsers();
      fetchPreviouslyWorkedUsers();
    }
  }, [isOpen, taskId]);

  useEffect(() => {
    if (isOpen) {
      setSelectedUserId('');
      setComment('');
      setReason('');
      setSelectedRole(currentRole);
    }
  }, [isOpen, currentRole]);

  const fetchUsers = async () => {
    try {
      console.log('Fetching users for task reallocation...');

      // Try multiple endpoints to find users
      let users: any[] = [];

      // First try: Get users by role (Staff)
      try {
        const staffResponse = await api.get('/users/by-role/Staff');
        console.log('Staff users response:', staffResponse.data);
        if (staffResponse.data.success) {
          users = [...users, ...staffResponse.data.data];
        }
      } catch (staffError) {
        console.log('Failed to fetch staff users:', staffError.response?.data);
      }

      // Second try: Get users by role (Manager)
      try {
        const managerResponse = await api.get('/users/by-role/Manager');
        console.log('Manager users response:', managerResponse.data);
        if (managerResponse.data.success) {
          users = [...users, ...managerResponse.data.data];
        }
      } catch (managerError) {
        console.log('Failed to fetch manager users:', managerError.response?.data);
      }

      // Third try: Get all users (fallback)
      if (users.length === 0) {
        try {
          const allUsersResponse = await api.get('/users', {
            params: {
              limit: 100,
              status: 'active'
            }
          });
          console.log('All users response:', allUsersResponse.data);
          if (allUsersResponse.data.success && allUsersResponse.data.data.users) {
            const eligibleUsers = allUsersResponse.data.data.users.filter((user: any) =>
              user.roles && user.roles.some((role: string) =>
                ['Staff', 'Manager'].includes(role)
              )
            );
            users = eligibleUsers;
          }
        } catch (allUsersError) {
          console.log('Failed to fetch all users:', allUsersError.response?.data);
        }
      }

      // Filter out the current user from the list
      const filteredUsers = users.filter(user => user.id !== currentUser?.id);

      console.log('Final users list (excluding current user):', filteredUsers);
      setUsers(filteredUsers);

      if (filteredUsers.length === 0) {
        toast.error('No other eligible users found for task reallocation');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchPreviouslyWorkedUsers = async () => {
    try {
      const response = await api.get(`/tasks/${taskId}/previously-worked-users`);
      if (response.data.success) {
        setPreviouslyWorkedUsers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching previously worked users:', error);
    }
  };

  const handleReallocate = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user to reallocate the task to');
      return;
    }

    if (!comment.trim()) {
      toast.error('Please provide a comment for the reallocation');
      return;
    }

    setLoading(true);
    try {
      const response = await api.patch(`/tasks/${taskId}/reallocate`, {
        toUserId: selectedUserId,
        role: selectedRole,
        comment: comment.trim(),
        reason: reason.trim() || undefined
      });

      if (response.data.success) {
        toast.success('Task reallocated successfully');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Error reallocating task:', error);
      toast.error(error.response?.data?.message || 'Failed to reallocate task');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <ArrowRight className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Reallocate Task
              </h2>
              <p className="text-sm text-gray-600">
                {taskTitle && `Task: ${taskTitle}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role to Reallocate
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as 'preparer' | 'reviewer' | 'submitter')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="preparer">Preparer</option>
              <option value="reviewer">Reviewer</option>
              <option value="submitter">Submitter</option>
            </select>
          </div>

          {/* User Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reallocate to User *
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select a user</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.fullName} ({user.email})
                </option>
              ))}
            </select>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comment *
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Please provide a reason for reallocating this task..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              required
            />
          </div>

          {/* Reason (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Reason (Optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Workload balancing, Expertise required, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Previously Worked Users */}
          {previouslyWorkedUsers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Users className="w-4 h-4 inline mr-2" />
                Previously Worked Users
              </label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                <div className="space-y-2">
                  {previouslyWorkedUsers.map(user => (
                    <div key={user.id} className="flex items-center justify-between py-2 px-3 bg-white rounded border">
                      <div className="flex items-center gap-3">
                        <User className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.fullName}
                            {user.isCurrentlyAssigned && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Currently Assigned
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            Roles: {user.roles.join(', ')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(user.lastWorkedAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleReallocate}
            disabled={loading || !selectedUserId || !comment.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Reallocating...
              </>
            ) : (
              <>
                <ArrowRight className="w-4 h-4" />
                Reallocate Task
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
